import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { useToast } from '../../components/ui/Toast';
import { DataGrid, type Column } from '../../components/ui/DataGrid';
import { formatEUR } from '../../lib/format';
import { PageHeader } from '../../components/ui/PageHeader';

interface Row {
  period: string;
  vat_rate: string;
  b2b_vat: string;
  b2c_vat: string;
  intra_eu_vat: string;
  cn_vat: string;
  discrepancy: string;
}

/** Screen 65 — VAT reconciliation. Single endpoint returns rows + active alerts. */
export default function VatReconciliation() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [alerts, setAlerts] = useState<Row[]>([]);
  useEffect(() => {
    apiClient
      .get<{ data: { rows: Row[]; alerts: Row[] } }>('/einvoice/reports/vat-reconciliation')
      .then((r) => {
        setRows(r.data.data.rows);
        setAlerts(r.data.data.alerts);
      })
      .catch(showApiError);
  }, [showApiError]);

  const cols: Column<Row>[] = [
    { key: 'period', header: t('einvoice.vat.period') },
    { key: 'vat_rate', header: t('einvoice.vat.vatPct') },
    {
      key: 'b2b_vat',
      header: t('einvoice.vat.b2bVat'),
      render: (r) => `${formatEUR(Number(r.b2b_vat))}`,
    },
    {
      key: 'b2c_vat',
      header: t('einvoice.vat.b2cVat'),
      render: (r) => `${formatEUR(Number(r.b2c_vat))}`,
    },
    {
      key: 'intra_eu_vat',
      header: t('einvoice.vat.intraEu'),
      render: (r) => `${formatEUR(Number(r.intra_eu_vat))}`,
    },
    {
      key: 'cn_vat',
      header: t('einvoice.vat.creditNotes'),
      render: (r) => `${formatEUR(Number(r.cn_vat))}`,
    },
    {
      key: 'discrepancy',
      header: t('einvoice.vat.drift'),
      render: (r) => {
        const v = Number(r.discrepancy);
        return (
          <span className={Math.abs(v) > 1 ? 'text-destructive font-semibold' : ''}>
            {formatEUR(v)}
          </span>
        );
      },
    },
  ];
  return (
    <div className="space-y-4">
      <PageHeader title={t('einvoice.vat.title')} />
      {alerts.length > 0 && (
        <div className="border-l-4 border-destructive bg-destructive/10 p-3 text-sm">
          {t('einvoice.vat.alert', { count: alerts.length })}
        </div>
      )}
      <DataGrid columns={cols} rows={rows} rowKey={(r) => `${r.period}-${r.vat_rate}`} />
    </div>
  );
}
